import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { interval } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { LayoutService } from '@layout/services/layout.service';
import { PageDirectionEnum } from '@shared/enums/global.enum';
import { AuthHighlightsAudience } from '@shared-features/enums/auth-highlights-audience.enum';
import { BaseComponent } from '../base-component/base.component';
import { highlightImageAnimation, highlightTextAnimation } from './auth-highlights.animation';

interface AuthHighlight {
  titleKey: string;
  descKey: string;
  image: string;
}

const IMAGES_PATH = 'assets/images/auth-highlights';
const HIGHLIGHT_IMAGES = ['warehouse-forklift', 'delivery-truck', 'shipping-container'];

// Both audiences show the same artwork in the same order; only the copy differs,
// so the slides are derived from one image list rather than kept in two arrays.
const buildHighlights = (audience: AuthHighlightsAudience): AuthHighlight[] =>
  HIGHLIGHT_IMAGES.map((name, index) => ({
    titleKey: `auth.Highlights.${audience}.${index + 1}.Title`,
    descKey: `auth.Highlights.${audience}.${index + 1}.Description`,
    image: `${IMAGES_PATH}/${name}.webp`,
  }));

@Component({
  selector: 'app-auth-highlights',
  templateUrl: './auth-highlights.component.html',
  styleUrls: ['./auth-highlights.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [highlightTextAnimation, highlightImageAnimation],
})
export class AuthHighlightsComponent extends BaseComponent implements OnInit {
  // Each page knows its own audience; the host cannot tell them apart, because
  // the fleet registration route is reachable on every host.
  @Input() set audience(value: AuthHighlightsAudience) {
    this.highlights = buildHighlights(value);
  }

  highlights: AuthHighlight[] = buildHighlights(AuthHighlightsAudience.Shipper);

  readonly slideParams$ = this.layoutService.websiteDirection$.pipe(
    map((direction) => (direction === PageDirectionEnum.Rtl ? { edge: '-100%' } : { edge: '100%' }))
  );

  activeIndex = 0;

  private readonly ROTATION_IN_MS = 7000;

  constructor(private layoutService: LayoutService, private changeDetector: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    // WCAG 2.2.2: users who ask for reduced motion get a static first slide
    // instead of content that keeps moving with no way to stop it.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    interval(this.ROTATION_IN_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.activeIndex = (this.activeIndex + 1) % this.highlights.length;
        this.changeDetector.markForCheck();
      });
  }
}
